import {
  Schema as SystemIcon,
  Settings as SubsystemIcon,
  Category as ConstructionTypeIcon,
} from '@mui/icons-material';
import { Cascader, CascaderOption } from '@nohara-dxdev/react-core';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettingValues } from '@/features/settings';
import { SettingIdValue } from '@/types/api';

import { useSystems } from '../api/get-systems';

type ConstructionTypeId = string;
type SystemId = string;
type SubsystemId = string;
export type SystemPathIds =
  | [ConstructionTypeId, SystemId, SubsystemId]
  | [ConstructionTypeId, SystemId]
  | [ConstructionTypeId]
  | [];

export interface SystemSelectProps {
  systemPathIds: SystemPathIds;
  onChange: (systemPathIds: SystemPathIds) => void;
  onClear?: () => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  constructionTypeIdOptions?: string[];
}

export const SystemSelect = ({
  systemPathIds,
  onChange,
  onClear,
  error,
  placeholder,
  disabled,
  constructionTypeIdOptions = [],
}: SystemSelectProps) => {
  const { t } = useTranslation();
  const {
    data: systems = [],
    isLoading: isLoadingSystems,
    error: errorLoadingSystems,
  } = useSystems();
  const { getSettingValue } = useSettingValues();

  // Get construction types from settings
  const constructionTypes = getSettingValue<SettingIdValue<string>[]>('constructionTypes', []);

  // Transform data into 3-level Cascader format: ConstructionType → System → Subsystem
  const cascaderOptions = useMemo(() => {
    return constructionTypes.map((constructionType) => ({
      id: constructionType.id,
      label: constructionType.value,
      value: constructionType,
      icon: <ConstructionTypeIcon />,
      disabled: Boolean(
        constructionTypeIdOptions.length &&
          !constructionTypeIdOptions.includes(constructionType.id),
      ),
      children: systems
        .filter((system) => system.constructionTypeId === constructionType.id)
        .map((system) => ({
          id: system.id,
          label: system.name,
          value: system,
          icon: <SystemIcon />,
          children: system.subsystems.map((subsystem) => ({
            id: subsystem.id,
            label: subsystem.name,
            value: subsystem,
            icon: <SubsystemIcon />,
          })),
        })),
    }));
  }, [constructionTypes, systems, constructionTypeIdOptions]);

  // Find selected path based on constructionTypeId and currentlySystemValues
  const selectedPath = useMemo(() => {
    if (!systemPathIds.length) return [];

    const [constructionTypeId, systemId, subsystemId] = systemPathIds;
    const path: CascaderOption[] = [];

    // Find the construction type
    const constructionTypeOption = cascaderOptions.find((ct) => ct.id === constructionTypeId);
    if (!constructionTypeOption) return path;
    path.push(constructionTypeOption);

    // Find the system within the construction type
    const systemOption = constructionTypeOption.children.find((s) => s.id === systemId);
    if (!systemOption) return path;
    path.push(systemOption);

    // Find the subsystem within the system
    const subsystemOption = systemOption.children.find((s) => s.id === subsystemId);
    if (subsystemOption) path.push(subsystemOption);

    return path;
  }, [systemPathIds, cascaderOptions]);

  // Handle cascader selection changes
  const handleCascaderChange = useCallback(
    (value: CascaderOption[]) => {
      // Convert CascaderOption[] to LocationPathIds
      onChange(value.map((option: CascaderOption<unknown>) => option.id) as SystemPathIds);
      return true;
    },
    [onChange],
  );

  const searchTextExtractor = useCallback((option: CascaderOption) => {
    return option.label;
  }, []);

  // Determine error message to display
  const loadErrorMessage = t(
    'systems.nested.errorLoadingSystems',
    'Failed to load systems. Please check if construction types are configured in settings.',
  );
  const errorMessage = errorLoadingSystems ? errorLoadingSystems.message : error;

  return (
    <Cascader
      options={cascaderOptions}
      value={selectedPath}
      onChange={handleCascaderChange}
      placeholder={placeholder ?? t('systems.nested.selectSubsystem')}
      loading={isLoadingSystems}
      error={errorMessage}
      disabled={disabled}
      columnHeaders={[
        t('systems.nested.constructionTypes'),
        t('systems.nested.systems'),
        t('systems.nested.subsystems'),
      ]}
      emptyText={errorLoadingSystems ? loadErrorMessage : t('systems.nested.noSystems')}
      maxHeight={400}
      minHeight={200}
      searchable={true}
      searchTextExtractor={searchTextExtractor}
      searchEmptyText={t('systems.nested.noSearchResults')}
      onClear={onClear}
    />
  );
};
